import { useState, useEffect } from 'react';
import { useConfig } from '../../providers/configProvider';
import { useToast } from '../../providers/toastProvider';
import { getInformationPageContent, updateInformationPage, createInformationPage, deleteInformationPage } from '../../api/information-page-api';
import { useLanguage } from '../../providers/preferenceProvider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faHeading } from '@fortawesome/free-solid-svg-icons';
import '../../css/overlay.css';
import { ACL } from '../../acls';

const EditPage = ({ isOpen, onClose, page, createNew, onRefresh }) => {
  const { t } = useLanguage();
  const { isAuthorized } = useConfig();
  const { showToast, showActionToast, showLoading, hideLoading } = useToast();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [fieldErrors, setFieldErrors] = useState([]);

  useEffect(() => {
    if (!isOpen) return;
    if (page) {
      setTitle(page.title || '');

      getInformationPageContent(page.id).then((response) => {
        setContent(response.data.content);
      }).catch(() => { });
    } else {
      setTitle('');
      setContent('');
    }
  }, [isOpen, page]);

  if (!isOpen) return null;

  const closeOverlay = () => {
    setTitle('');
    setContent('');
    onClose();
  }

  const saveChange = () => {
    showLoading();

    const newErrors = [];
    if (!title) newErrors.push("title");
    if (!content) newErrors.push("content");
    setFieldErrors(newErrors);

    if (newErrors.length > 0) {
      showToast(t.toast.fill_all_fields, 'error');
      hideLoading();
      return;
    }

    if (createNew) {
      createInformationPage(title, content).then(() => {
        if (onRefresh) onRefresh();
        hideLoading();
        closeOverlay();
        showToast(t.page + " " + t.toast.created_success, 'success');
      }).catch(() => {
        hideLoading();
      });
    } else {
      updateInformationPage(page.id, title, content).then(() => {
        if (onRefresh) onRefresh();
        hideLoading();
        closeOverlay();
        showToast(t.page + " " + t.toast.updated_success, 'success');
      }).catch(() => {
        hideLoading();
      });
    }
  }

  const deleteConfirmation = () => {
    showActionToast(
      t.toast.confirm_delete_general.replace('{ph_name}', page.title),
      handleDeletePage,
      null
    );
  }

  const handleDeletePage = () => {
    showLoading();
    deleteInformationPage(page.id).then(() => {
      hideLoading();
      onRefresh();
      closeOverlay();
      showToast(t.page + " " + t.toast.deleted_success, 'success');
    }).catch(() => {
      hideLoading();
    });
  }

  return (
    <div className="overlay-backdrop" onClick={onClose}>
      <div className="overlay-content" onClick={(e) => e.stopPropagation()}>
        <div className="overlay-header">{createNew ? t.add : t.edit} {t.page}</div>
        <p>You can use Markdown to format the content: <a className='link' href='https://www.markdownguide.org/cheat-sheet/' target='_blank' rel="noreferrer">Cheat Sheet</a></p>
        <div className="overlay-admin-form flex flex-col gap-4 p-4">
          <div className="flex flex-row items-center gap-4">
            <label htmlFor="title" className="w-32"><FontAwesomeIcon icon={faHeading} /> {t.title}</label>
            <input type="text" id="title" name="title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="title..." className={"overlay-admin-input" + (fieldErrors.includes("title") ? " input-error" : "")} />
          </div>

          <div className="flex flex-row items-center gap-4">
            <label htmlFor="content" className="w-32"><FontAwesomeIcon icon={faPen} /> {t.content}</label>
            <textarea id="content" name="content" value={content} onChange={(e) => setContent(e.target.value)} placeholder="content..." className={"overlay-admin-textarea" + (fieldErrors.includes("content") ? " input-error" : "")} />
          </div>

        </div>
        <div className="flex flex-row mt-4 gap-2 justify-between">
          <div className='flex flex-row gap-2 align-items-center'>
            {!createNew && (
              <button className="btn" onClick={deleteConfirmation} disabled={!isAuthorized(ACL.COMPANY)}>{t.delete + " " + t.page}</button>
            )}
          </div>
          <div className="flex gap-2 justify-end">
            <button className="btn-close" onClick={closeOverlay}>{t.close}</button>
            <button className="btn" onClick={saveChange}>{t.confirm}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditPage;